/**
 * STORE-SPLIT slice 2 — the DXF-IMPORT shared machinery, extracted from
 * `editorStore.ts` (no behavior change; plan
 * 2026-06-06-fullstack-gap-closure.md STORE-SPLIT, UI_ARCHITECTURE.md §6.1).
 *
 * This module owns the import-family MODULE-LEVEL machinery that was
 * previously module-scoped inside `editorStore.ts`:
 *   - the `ImportProgress` slice type + `INITIAL_IMPORT_PROGRESS` seed,
 *   - the `HEAL_TRANSCRIPT_COMMAND` synthetic audit command,
 *   - the in-flight worker-import latch (`currentImportHandleRef`),
 *   - the streaming-import thresholds,
 *   - the coordinate-origin accumulator + array variant,
 *   - the sysvar / CECOLOR hydrate-in helpers,
 *   - the cache-aware empty-seed predicate (`isDocumentEffectivelyEmpty`),
 *   - the streamed-worker-document defensive defaults
 *     (`normalizeWorkerDocumentDefaults`),
 *   - the streaming viewport membership finalize
 *     (`finalizeStreamedViewportMembership`),
 *   - the import-merge revision bump set (`importMergeBumpForPriorSelection`).
 *
 * Consumers:
 *   - `editorStoreImportActions.ts` (slice 2) — the extracted
 *     `importDxfText` / `importDxfTextAsync` / import-progress action family;
 *   - `editorStore.ts` — the two still-inline `File`-handle import variants
 *     (`importDxfFileAsync` / `importDxfFileAsyncReplacing`; their extraction
 *     is the planned slice 3) plus the `reset()` / initializer
 *     `importProgress` seed. `editorStore.ts` RE-EXPORTS the previously-public
 *     helpers (`isDocumentEffectivelyEmpty`, `normalizeWorkerDocumentDefaults`,
 *     `finalizeStreamedViewportMembership`, `createCoordinateOriginAccumulator`,
 *     `ImportProgress`) so every external import path is unchanged.
 *
 * Shared machinery intentionally NOT moved (stays with its owner in
 * `editorStore.ts`):
 *   - `rebuildEntityMetadataIndexFromImport` / `…Async` — they bump the
 *     REACTIVE `entityMetadataIndexVersion` / `renderTopologyVersion` counters
 *     via `useEditorStore.setState`, a genuine store-owned seam (moving them
 *     here would create a `editorStoreImportHelpers → editorStore` back-edge
 *     and a dependency cycle). The import-actions slice receives them via its
 *     `deps` parameter.
 *   - `getEntityMetadataIndex` / `getEntityById` / `getCachedEntitiesForSpace`
 *     — render/pick accessors shared far beyond the import family.
 *
 * This module has NO edge back into `editorStore.ts` — the deps graph stays
 * acyclic (`editorStore → editorStoreImportHelpers`).
 */
import {
  applyViewportClipAndMembership,
  computeEntityWcsBounds,
  type BumpClassSet,
  type DrawingDocument,
  type DrawingEntity,
  type ImportPhaseName,
} from "@dxf-ai-editor/drawing-model";
import { type EditorCommand } from "@dxf-ai-editor/contracts";
import { useSysvarStore } from "../dynamicInput/sysvarStore";
import {
  buildCecolorHydration,
  buildHeaderSysvarHydration,
} from "./dxfGeometry";
import { useCeColorStore } from "./cecolorStore";
import { type WorkerImportResult } from "./dxfWorkerClient";
import {
  entityMetadataIndex,
  iterateSpaceEntity,
  peekDocumentMemoryHolder,
  snapshotSpaceEntities,
  updateCachedEntityInPlace,
} from "./mutationStore";
import { documentHasNoNonEntityHostState } from "./storeHelpers";

/**
 * Progress snapshot for an in-flight DXF import. `active` gates the
 * `ImportProgressDialog` visibility — when false, the dialog is
 * unmounted. `phase` identifies which stage of the
 * importPhases.IMPORT_PHASES pipeline is currently running so the
 * dialog can render the localized label.
 *
 * `percent` is the global 0-100 progress across all phases (computed
 * via `computeGlobalPercent`) so the bar moves monotonically even when
 * a new phase resets its own internal itemsProcessed counter to 0.
 *
 * `itemsProcessed / itemsTotal` are the current phase's raw counters —
 * the UI shows them as "X / Y" under the bar. For low-cardinality
 * phases (fetch, decode, sections, bridge, merge, sync) the counters
 * are 1/1 at completion so the "X / Y" widget has something to show.
 */
export type ImportProgress = {
  phase: ImportPhaseName | null;
  percent: number;
  itemsProcessed: number;
  itemsTotal: number;
  active: boolean;
};

export const INITIAL_IMPORT_PROGRESS: ImportProgress = {
  phase: null,
  percent: 0,
  itemsProcessed: 0,
  itemsTotal: 0,
  active: false,
};

/**
 * PR1b Phase 2a — synthetic command stamped on the audit/transcript event a
 * whole-document import emits when it heals dangling references (or when it
 * warns before an export). `source: "system"` marks it as an internal,
 * non-user-initiated event; the `text` is a stable, non-localised marker (the
 * USER-FACING summary is the localised `summary` argument to `pushAuditEvent`).
 */
export const HEAL_TRANSCRIPT_COMMAND: EditorCommand = {
  type: "commandline/submit",
  source: "system",
  text: "AUDIT (reference heal)",
};

/**
 * Module-scoped handle to the in-flight worker import, if any. Tracked
 * here (instead of inside zustand state) because the `WorkerImportResult`
 * holds a closure over the worker instance — it's not a plain value
 * suitable for a zustand slice. The store's `abortDxfImport` action
 * reads this handle and calls `abort()`, and a second concurrent
 * `importDxfTextAsync` call cancels the previous one before starting
 * a new parse.
 *
 * STORE-SPLIT slice 2 — the former `let currentImportHandle` module
 * variable is now a shared ref-object because the SAME single latch is
 * written by BOTH the extracted text-import actions
 * (`editorStoreImportActions.ts`) and the still-inline `File`-handle
 * variants in `editorStore.ts` (slice 3). Same one mutable cell, same
 * semantics — only the access spelling changed
 * (`currentImportHandleRef.current`).
 */
export const currentImportHandleRef: { current: WorkerImportResult | null } = {
  current: null,
};

/**
 * Phase 4 Wave 2 (П35Р follow-up — streaming-import enable) — file-size
 * threshold above which the worker's streaming-import path is used.
 *
 * Streaming saves the ~1.1 GB transient worker→main `postMessage`
 * structuredClone of the full DrawingDocument by routing entities to
 * the compact cache via `entity-chunk` messages as they are converted
 * in the worker. The trade-off is that small imports pay a small
 * per-chunk overhead (one postMessage + structured-clone of a 5000-
 * entity batch) that legacy bulk-populate avoids — pointless for sub-
 * megabyte fixtures with a few hundred entities.
 *
 * 2 MB picked empirically: at typical DXF entity density (~50 bytes
 * per group code), this corresponds to roughly 10k-20k entities —
 * crossing the threshold the legacy bulk-populate path starts to
 * matter (sub-2-MB files import in <500 ms even on the legacy path,
 * which is well under any progress-dialog refresh budget).
 *
 * NOT used in `importDxfTextAsync` — the text path's worker
 * (`runParse`) doesn't stream (`startDxfImportInWorker` accepts an
 * `onEntityChunk` for API symmetry but the worker side doesn't emit
 * chunks).
 */
export const STREAMING_IMPORT_FILE_SIZE_THRESHOLD = 2 * 1024 * 1024; // 2 MB

/**
 * Phase 4 Wave 2 (П35Р follow-up — streaming-import enable) — emit a
 * progress tick at most every N chunks. Phase 1.k (PR #85) traced part
 * of the 685k React #185 incident to store-update frequency: every
 * `set({ importProgress })` flushes a reactive subscription, and the
 * import-progress dialog re-renders on every flush. At STREAM_CHUNK_SIZE
 * 5000 a 685k drawing emits ~137 chunks; one progress tick per chunk
 * would over-fire by ~30× vs. the legacy phase-boundary cadence (which
 * fires ~5 times for a comparable run). 10 keeps the dialog smooth
 * (~14 ticks per 685k import = one per ~50k entities = visibly moving
 * bar without React thrash).
 */
export const STREAMING_PROGRESS_CHUNK_INTERVAL = 10;

/**
 * PR-A — coordinate-origin compute for the Three.js renderer.
 *
 * Walks every entity coming out of the DXF worker and unions its WCS
 * bounding box (`computeEntityWcsBounds` — see {@link push}). The bbox of
 * those boxes is centred and rounded to a multiple of 1e6 — that integer
 * granularity is what makes the offset reproducible across imports
 * of the same drawing (same fixture → same origin → same DXF
 * round-trip output bytes).
 *
 * post-V9 Section 0 Iter 6.D — `space.entities` always populated for
 * both cap-busting and below-cap imports thanks to slim-state-as-pointer
 * (Phase 3b PR #6). Drop the legacy Map fallback path entirely; the
 * caller flattens every space's entities into the single array passed
 * here. Returning `null` signals "no usable coordinates" — callers
 * skip the setCoordinateOrigin call so the adapter falls back to
 * identity (no offset).
 */
/**
 * Phase 4 Wave 2 Task 4 — incremental bbox accumulator factory.
 *
 * Streaming-import callers feed entities to {@link push} as they arrive
 * from the worker chunk callback, then call {@link finalize} after the
 * import settles to get the rounded origin. This avoids materialising
 * the full entity array in main-thread memory just to compute the
 * bbox — critical for 685k+ entity drawings where the temporary array
 * would itself approach the worker peak we are trying to eliminate.
 *
 * The legacy {@link computeCoordinateOriginForImport} array variant is
 * implemented in terms of this accumulator so both paths share the
 * exact same per-entity coordinate-extraction logic and produce
 * identical origin values for the same input.
 */
export function createCoordinateOriginAccumulator(): {
  push: (entity: DrawingEntity) => void;
  finalize: () => { x: number; y: number; z: number } | null;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let seenAny = false;

  // Common point reader — pulls (x, y) from any object with those
  // numeric keys. Used by every entity-kind branch below.
  const consider = (p: { x: number; y: number } | null | undefined): void => {
    if (!p) return;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
    seenAny = true;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  };

  const push = (entity: DrawingEntity): void => {
    // ONE truth for "where is this entity in WCS": `computeEntityWcsBounds`,
    // the same walk the spatial index, the viewport-membership test and the
    // WCS bounds surface read (`drawing-model/wcsBounds.ts`).
    //
    // This used to be a hand-written per-kind switch that read "the well-known
    // coordinate fields" of the dozen kinds someone had got round to, with a
    // `default:` that silently skipped the rest — so `Hatch`, `Wipeout`,
    // `Revision Cloud`, `Rectangular Associative Array` and (once they were
    // admitted to the streamed store) the heavy `Polyline`, `Table` and
    // `OLE Frame` contributed NOTHING. A drawing composed only of those kinds
    // at survey-scale coordinates finalised with NO origin at all, and a mixed
    // one could pick an origin from whichever unrelated primitive happened to
    // be recognised — leaving the real geometry at its full magnitude in the
    // Float32 GPU buffers (this module's siblings in
    // `scene3d/coordinateOrigin.ts` document why that jitters).
    //
    // Routing through the shared walk also fixes WHICH coordinates are read: a
    // 2D heavy POLYLINE stores OCS vertices, and only the shared walk applies
    // the arbitrary-axis lift to WCS. Reading `vertex.location` raw would have
    // accumulated a point the drawing does not contain.
    //
    // And it makes the gap unrepeatable: `computeEntityWcsBounds`'s switch
    // ends in an `assertNever`, so a new arm of `drawingEntitySchema` with no
    // bounds case is a tsc error rather than another silent skip. The census
    // test (`__tests__/coordinateOriginCensus.test.ts`) pins the other half —
    // that every discriminant actually CONTRIBUTES here.
    const box = computeEntityWcsBounds(entity);
    if (box) {
      consider({ x: box.minX, y: box.minY });
      consider({ x: box.maxX, y: box.maxY });
      return;
    }
    // Honest absence, not a skip: the two BLOCK-BACKED arms (`Block` and
    // `Table`) return `null` without a block-definition table, which the
    // streaming import does not have while chunks are still arriving. Their
    // insertion point is where the content is anchored, so it is still the
    // right coordinate to weigh the origin with — exactly what the previous
    // `case "Block"` did, and strictly more than the previous code gave a
    // `Table`. Every other arm returns `null` only when the entity carries no
    // coordinates at all, and then there is nothing to contribute.
    consider("insertionPoint" in entity ? entity.insertionPoint : null);
  };

  const finalize = (): { x: number; y: number; z: number } | null => {
    if (
      !seenAny ||
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return null;
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    // Round to nearest 1e6 so subsequent imports of the same fixture
    // pick the same origin (deterministic round-trip).
    const round = (v: number): number => Math.round(v / 1e6) * 1e6;
    return { x: round(cx), y: round(cy), z: 0 };
  };

  return { push, finalize };
}

export function computeCoordinateOriginForImport(
  entities: readonly DrawingEntity[],
): { x: number; y: number; z: number } | null {
  // post-V9 Section 0 Iter 6.D — single-arg signature; `space.entities`
  // is always populated by slim-state-as-pointer (Phase 3b PR #6) so
  // the caller can flat-map every space's entities directly.
  // NB: `array.push(...big)` spreads `big.length` function arguments
  // and triggers `RangeError: Maximum call stack size exceeded` past
  // ~10k entries — for-of avoids the stack blowup on 685k-entity DXFs.
  if (entities.length === 0) return null;
  // Phase 4 Wave 2 Task 4 — delegate to the streaming-friendly
  // accumulator factory so streaming-import and array-import paths
  // produce identical origin values.
  const acc = createCoordinateOriginAccumulator();
  for (const entity of entities) acc.push(entity);
  return acc.finalize();
}

/**
 * Hydrate the live sysvar store FROM a freshly-imported document's HEADER vars
 * (DYN* family + the typed MIRRTEXT / OSMODE fields). Codex #342 F2: every
 * store/window import entry point (importDxfText / …Async / …FileAsyncReplacing)
 * must pair this hydrate-IN with the export project-OUT (`applyLiveHeaderSysvars`)
 * — otherwise a DevTools/automation import of a DXF with non-default
 * $MIRRTEXT / $OSMODE, then export without an intervening SETVAR, projects stale
 * live defaults over the imported header and recreates the NEW-1 save-corruption.
 * Idempotent (App.tsx's file-open wrapper may also call it for the same doc).
 * The hydrate-in / project-out seam lives in ONE module (`dxfGeometry.ts`).
 */
export function hydrateSysvarsFromImportedDocument(doc: DrawingDocument): void {
  const hydrateVars = buildHeaderSysvarHydration(doc);
  if (Object.keys(hydrateVars).length > 0) {
    useSysvarStore.getState().hydrateFromDxf(hydrateVars);
  }
}

/**
 * Hydrate the live `cecolorStore` FROM a freshly-imported document's `$CECOLOR`
 * — the union sibling of {@link hydrateSysvarsFromImportedDocument} (CECOLOR
 * cannot ride the integer-only sysvar schema). Pairs the hydrate-IN with the
 * export project-OUT (`applyLiveCeColor` in `exportDxfText` + App.tsx's export
 * seam) so a DXF carrying a non-default $CECOLOR, then exported without an
 * intervening COLOR, projects the file's current colour — not a stale live
 * default. The merged document is the MERGE result, so `mergeCeColor` has
 * already kept the live colour on INSERT (this is a no-op there) and taken the
 * imported colour on an empty-seed OPEN — making hydration a no-op-on-INSERT by
 * construction (the round-7 MIRRTEXT/OSMODE INSERT-preserve lesson). Idempotent
 * (App.tsx's file-open wrapper may also call it for the same doc).
 */
export function hydrateCeColorFromImportedDocument(doc: DrawingDocument): void {
  useCeColorStore.getState().hydrateCeColor(buildCecolorHydration(doc));
}

/**
 * Codex round 4 #9 (PR #87) — cache-aware empty-seed predicate.
 *
 * The merge-skip optimisation in `importDxfTextAsync` / `importDxfFileAsync`
 * historically checked `spaces.every(s => s.entities.length === 0)` to
 * detect a fresh seed doc and bypass `mergeImportedGeometryDocumentAsync`.
 * After Phase 4 Wave 2 wires streaming on OPEN, a fully-loaded 685k-entity
 * drawing also has empty inline arrays (entities live only in the compact
 * cache + metadata index per the streaming contract). A subsequent INSERT
 * would mis-classify the doc as "empty" and REPLACE it with the inserted
 * file, then repopulate cache from the replacement — silent data loss.
 *
 * Pattern matches the round-2 `currentDocHasEntities` gate (line ~2812):
 * combine inline arrays + `entityMetadataIndex.size` + the sticky
 * `cachePopulatedOnce` flag. ANY truthy signal → doc is non-empty, take
 * the full merge path so collisions are reconciled.
 */
export function isDocumentEffectivelyEmpty(doc: DrawingDocument): boolean {
  if (doc.spaces.some((s) => s.entities.length > 0)) return false;
  if (entityMetadataIndex.size > 0) return false;
  const holder = peekDocumentMemoryHolder(doc.id);
  if (holder?.cachePopulatedOnce === true) return false;

  // FIX 2 (P1, host data loss) — the entity-only checks above are NECESSARY but
  // NOT SUFFICIENT. A host that drew + erased entities (so its inline arrays /
  // metadata index / cache flag are all empty) but ran BLOCK / WBLOCK / LAYOUT /
  // STYLE / DIMSTYLE / XATTACH still carries NON-ENTITY host state in
  // `blocks` / `blockRecords` / `layouts` / `annotationScales` / `xrefRegistry` /
  // `styleTables`. Mis-classing such a host as a fresh seed routes the caller
  // into the REPLACE branch (`{...importedGeometryDocument, ...}`) which DROPS
  // that host state instead of `mergeImportedGeometryDocument(Async)` which UNIONs
  // host + imported -- worst case a user-defined BLOCK definition + its
  // BLOCK_RECORD vanish on the next OPEN, dangling any INSERT that referenced it.
  //
  // The structural (cache-independent) signal is SHARED with the storeHelpers
  // merge default (`documentHasNoNonEntityHostState`) so the two classifications
  // cannot diverge. It is tightened to be EXACTLY narrow enough that a pristine
  // fresh seed (`buildInitialGeometryDocument`: empty `layouts`/`blocks`/
  // `blockRecords`/`xrefRegistry` maps, `annotationScales: []`, default
  // `styleTables`, no `provenance`) still returns TRUE -- the streaming-OPEN
  // merge-skip optimisation must not regress (asserted by the SACRED test).
  if (!documentHasNoNonEntityHostState(doc)) return false;

  return true;
}

/**
 * Codex round 4 #10 (PR #87) — defensive defaults for streamed worker
 * documents.
 *
 * The streaming worker path can omit optional collection fields
 * (`annotationScales`, `blocks`, `blockRecords`, `xrefRegistry`) when the
 * chunked sections don't carry them. Downstream reducers + UI paths assume
 * these are present (e.g. `geometryReducerAnnotative` calls
 * `geometryDocument.annotationScales.find(...)`), so an unnormalised
 * commit causes `TypeError: Cannot read properties of undefined` on the
 * first annotative action / layer-manager open after import.
 *
 * Used by BOTH the OPEN streaming success branch (`importDxfFileAsyncReplacing`)
 * and the INSERT streaming success branch (`importDxfFileAsync`) so the two
 * paths cannot drift again.
 */
export function normalizeWorkerDocumentDefaults(
  workerDocument: DrawingDocument,
): DrawingDocument {
  return {
    ...workerDocument,
    annotationScales: workerDocument.annotationScales ?? [],
    blocks: workerDocument.blocks ?? {},
    blockRecords: workerDocument.blockRecords ?? {},
    xrefRegistry: workerDocument.xrefRegistry ?? {},
  };
}

/**
 * EPIC-VIEWPORT-CLIP SLICE 3 — main-thread membership finalize for the STREAMING
 * import path (full-fidelity import, ПРИОРИТЕТ #1).
 *
 * On the streaming path the worker discards every model-space entity to bound
 * memory, so the slice-3 membership pass inside the bridge sees an EMPTY model
 * space and leaves `childEntityIds` unset. It ALSO cannot resolve a native
 * group-340 VPCLIP boundary (the referenced paper-space boundary entity is
 * streamed out, not retained in the bridge shell). Both kinds of input live ONLY
 * in the main-thread compact cache after streaming. This re-runs, from the cache:
 *   - the MEMBERSHIP half (model-space entities → each VP's `childEntityIds`), and
 *   - the NATIVE group-340 boundary clip half (paper-space entities → `clipPath`,
 *     Codex #326 P2 worker:372).
 * The SPATIAL_FILTER clip half is SKIPPED — the worker already resolved it (the
 * raw OBJECTS section + retained typed viewports were intact inside the worker;
 * that clipPath round-trips on the document).
 *
 * Mutates the viewport entities IN PLACE on `document`. Then writes each
 * finalized viewport BACK into the compact cache (Codex #326 P2 editorStore:949)
 * so a later `wrapSpaceAsReadThrough` rematerialization (which treats streamed
 * spaces as cache-authoritative) does NOT drop the resolved `clipPath` /
 * `childEntityIds`. Returns true when at least one viewport was finalized so
 * callers can bump a render version. No-op (returns false) when the document
 * carries no viewport.
 */
export function finalizeStreamedViewportMembership(
  document: DrawingDocument,
  docId: string,
): boolean {
  const modelSpace = document.spaces.find((s) => s.kind === "model");
  const paperSpacesWithViewports = document.spaces.filter(
    (space) =>
      space.kind !== "model" &&
      space.entities.some((e) => e.type === "Viewport"),
  );
  if (paperSpacesWithViewports.length === 0) return false;
  // Re-read the streamed MODEL entities back from the compact cache (the
  // streaming source of truth — they were drained from the inline
  // `space.entities` array) as a LAZY generator, NOT a materialised snapshot.
  // `buildViewportMembershipIndex` walks it once and retains only a slim
  // `{id, AABB}` per entity, so each `DrawingEntity` is GC-eligible immediately
  // after — the peak heap never holds the full per-space `DrawingEntity[]`
  // (hundreds of MB on a large model space), preserving the streaming memory cap
  // (Codex #326 r3 P2 #4). The membership pass is a cheap no-op when the
  // generator is empty (every VP gets an empty member list), so no `.length`
  // probe — which would force the generator to be pre-materialised — is needed.
  const modelSpaceEntities: Iterable<DrawingEntity> = modelSpace
    ? iterateSpaceEntity(docId, modelSpace.id)
    : [];
  // Paper-space boundary entities for the native-340 clip lookup: every
  // non-model space that holds a clipped viewport (the boundary is a paper-space
  // sibling of its viewport, possibly on the same layout). These are bounded by
  // paper-space size (a handful of entities), not the model space, so a snapshot
  // here is cheap — the index built from them needs random-access lookups anyway.
  const paperSpaceEntities: DrawingEntity[] = [];
  for (const space of paperSpacesWithViewports) {
    const cached = snapshotSpaceEntities(docId, space.id);
    for (const e of cached) paperSpaceEntities.push(e);
  }
  const clipFinalize = applyViewportClipAndMembership(
    document,
    null,
    document.blocks ?? {},
    {
      // SPATIAL_FILTER half already resolved in the worker — keep it.
      skipClip: true,
      // Native group-340 boundary half runs here from the cache.
      skipNativeBoundaryClip: false,
      paperSpaceEntities,
      // Membership half runs over the lazy model generator (no-op if empty).
      modelSpaceEntities,
    },
  );
  // Codex #326 r4 #3 — on the streaming path the worker SKIPS native-340 boundary
  // resolution (`skipNativeBoundaryClip: true`), so THIS main-thread finalize is
  // the ONLY place that can surface an unsupported/missing native boundary (a
  // CIRCLE/ELLIPSE/closed-SPLINE boundary that needs its own tessellation, or a
  // dangling 340). The worker already folded any SPATIAL_FILTER-half orphans into
  // `document.importDiagnostics`; merge the native-340 orphan count returned here
  // ON TOP of that so a streamed drawing with an unsupported native boundary
  // carries the same orphan warning the non-streaming path emits (parity), instead
  // of rendering rectangular with NO diagnostic. ADD (not overwrite) to preserve
  // the worker's SPATIAL_FILTER orphan count; emit the field only when non-zero so
  // a clean import carries no `importDiagnostics` (matches the bridge convention).
  if (clipFinalize.orphanViewportClipReferences > 0) {
    const existing = document.importDiagnostics ?? {};
    document.importDiagnostics = {
      ...existing,
      orphanViewportClipReferences:
        (existing.orphanViewportClipReferences ?? 0) +
        clipFinalize.orphanViewportClipReferences,
    };
  }
  // Write the finalized viewports back into the compact cache so read-through
  // rematerialization keeps the resolved fields (Codex #326 P2 editorStore:949).
  for (const space of paperSpacesWithViewports) {
    for (const e of space.entities) {
      if (e.type === "Viewport") {
        updateCachedEntityInPlace(docId, e);
      }
    }
  }
  return true;
}

/**
 * ABB-S1 G3 #5 — the bump set for an import MERGE (`importDxfText` /
 * `importDxfTextAsync` / `importDxfFileAsync` INSERT-style merge). A merge folds
 * the imported document's entities AND named-object tables (layers/blocks/styles/
 * xref) into the LIVE document IN PLACE — it is a MUTATION, not a document SWAP,
 * so it DUAL-BUMPs geometry + table (+ snapshot once) but does NOT mint a new
 * `documentEpoch` (the doc id is preserved verbatim). The file-OPEN replacement
 * path (`importDxfFileAsyncReplacing`) is the only swap → it `mintNewDocumentEpoch`.
 *
 * round-4 F3 — ALSO bumps `selection`: every INSERT-style merge path rebuilds
 * shell state through `syncShellStateFromMergedGeometryDocument`, which
 * UNCONDITIONALLY clears `selection.entityIds` (the prior pickset refers to
 * entities that no longer exist / got remapped). An import that wipes a
 * non-empty selection IS a selection change, so `selectionRevision` must advance
 * — else an agent holding the old `baseSelectionRevision` misses that its pickset
 * was invalidated.
 *
 * PR #362 round-5 (F2 ROOT) — the selection bump is CONDITIONAL on the prior
 * selection actually having been non-empty: clearing an ALREADY-EMPTY selection
 * is a no-op (no membership change), so it must NOT bump `selectionRevision` (a
 * phantom bump misleads an agent gating on `baseSelectionRevision`). The
 * geometry + table + snapshot classes always bump (the merge folds entities +
 * tables in place). `selectionBumpForImportMerge` computes the per-import set.
 */
const IMPORT_MERGE_BUMP_NO_SELECTION: BumpClassSet = Object.freeze({
  geometry: true,
  table: true,
  context: false,
  selection: false,
});

const IMPORT_MERGE_BUMP_WITH_SELECTION: BumpClassSet = Object.freeze({
  geometry: true,
  table: true,
  context: false,
  selection: true,
});

/**
 * PR #362 round-5 (F2) — the import-merge bump set, with `selection` included
 * ONLY when the merge actually CHANGES the selection. The merge clears
 * `selection.entityIds` unconditionally, so a real selection change happens iff
 * the prior selection was NON-EMPTY (empty → empty is a no-op). Geometry + table
 * + snapshot always bump.
 */
export function importMergeBumpForPriorSelection(
  priorSelectionIds: readonly string[],
): BumpClassSet {
  return priorSelectionIds.length > 0
    ? IMPORT_MERGE_BUMP_WITH_SELECTION
    : IMPORT_MERGE_BUMP_NO_SELECTION;
}
